import React from 'react'

import { useFormContext, Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { AlegraLocationOption } from '@/core/hooks/payment/useAlegraLocations'
import { Input } from '@/shared/components/atoms/Input'
import { Label } from '@/shared/components/atoms/Label/Label'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'
import { Skeleton } from '@/shared/components/atoms/Skeleton'

import { BillingInformationFormData } from '../BillingInformation.types'

export interface AlegraChlAddressFieldsProps {
	regions: AlegraLocationOption[]
	communes: AlegraLocationOption[]
	isLoadingCommunes: boolean
	selectedRegion: string
}

export const AlegraChlAddressFields: React.FC<AlegraChlAddressFieldsProps> = ({
	regions,
	communes,
	isLoadingCommunes,
	selectedRegion,
}) => {
	const { t } = useTranslation('common')
	const {
		control,
		setValue,
		formState: { errors },
	} = useFormContext<BillingInformationFormData>()

	return (
		<>
			{/* State/Region */}
			<div className="flex flex-col gap-2">
				<Label htmlFor="state">
					{t('account.billing_information.state')} *
				</Label>
				<Controller
					name="state"
					control={control}
					render={({ field }) => (
						<Select
							value={field.value || ''}
							onValueChange={(value) => {
								field.onChange(value)
								setValue('city', '')
							}}
						>
							<SelectTrigger
								className="bg-input w-full"
								aria-invalid={!!errors.state}
							>
								<SelectValue
									placeholder={t('account.billing_information.select_state')}
								/>
							</SelectTrigger>
							<SelectContent>
								{regions.length > 0 ? (
									regions.map((region) => (
										<SelectItem key={region.id} value={region.value}>
											{region.value}
										</SelectItem>
									))
								) : (
									<div className="px-2 py-1.5 text-sm text-muted-foreground">
										{t('account.billing_information.loading_regions')}
									</div>
								)}
							</SelectContent>
						</Select>
					)}
				/>
				{errors.state && (
					<p className="text-sm text-destructive">{errors.state.message}</p>
				)}
			</div>

			{/* City/Commune */}
			<div className="flex flex-col gap-2">
				<Label htmlFor="city">{t('account.billing_information.city')} *</Label>
				{isLoadingCommunes ? (
					<Skeleton className="h-10 w-full" />
				) : (
					<Controller
						name="city"
						control={control}
						render={({ field }) => (
							<Select
								value={field.value || ''}
								onValueChange={field.onChange}
								disabled={!selectedRegion || isLoadingCommunes}
							>
								<SelectTrigger
									className="bg-input w-full"
									aria-invalid={!!errors.city}
								>
									<SelectValue
										placeholder={t('account.billing_information.select_city')}
									/>
								</SelectTrigger>
								<SelectContent>
									{communes.map((commune) => (
										<SelectItem key={commune.id} value={commune.value}>
											{commune.value}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						)}
					/>
				)}
				{errors.city && (
					<p className="text-sm text-destructive">{errors.city.message}</p>
				)}
			</div>

			{/* ZIP Code - Plain input for Chile */}
			<div className="flex flex-col gap-2">
				<Label htmlFor="zip">{t('account.billing_information.zip')} *</Label>
				<Controller
					name="zip"
					control={control}
					render={({ field }) => (
						<Input
							id="zip"
							{...field}
							className="bg-input"
							aria-invalid={!!errors.zip}
						/>
					)}
				/>
				{errors.zip && (
					<p className="text-sm text-destructive">{errors.zip.message}</p>
				)}
			</div>
		</>
	)
}
